import { API_URL } from '@constants';
import { useData } from '@hooks/useData';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
// import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/spaces';

export interface GetSpacesInput {
    search?: string;
    spaceStatusUuid?: string;
    featuredType?: string;
    spaceTypeCategory?: string;
    priceMin?: number;
    priceMax?: number;
    areaMin?: number;
    areaMax?: number;
    spaceTypeUuids?: string[];
    page?: number;
    perPage?: number;
    orderBy?: string;
    sortDesc?: boolean;
}

export interface GalleryItem {
    uuid: string;
    url: string;
    name?: string;
}

export interface DocumentItem {
    uuid: string;
    name: string;
    url: string;
}

export interface ContactItem {
    uuid: string;
    type: 'email' | 'telefone' | 'facebook' | 'instagram' | 'form';
    value: string;
}

export interface SpaceItem {
    uuid: string;
    title: string;
    isActive: boolean;
    description: string;
    address: string;
    latitude: string;
    longitude: string;
    areaM2: string;
    price: string;
    like: boolean;
    numberOfDoors: number;
    numberOfFloors: number;
    constructionYear: number;
    energyCertificate: {
        uuid: string;
        code: string;
    };
    spaceTypeCategory: {
        key: string;
        label: string;
    };
    featuredType: {
        key: string;
        label: string;
    };
    spaceType: {
        uuid: string;
        name: string;
        category: string;
    };
    spaceStatus: {
        uuid: string;
        name: string;
    };
    documents: DocumentItem[];
    galleries: GalleryItem[];
    contacts: ContactItem[];
    createdAt: string;
    updatedAt: string;
}

export interface GetSpacesSuccessResponse {
    status: number;
    success: boolean;
    message: string;
    data: SpaceItem[];
    links: {
        self: string;
    };
    recordsTotal: number;
}

/**
 * Requests endpoint to get paginated spaces.
 * Then saves it in Data Context
 * @param own If true, only spaces owned by the current user will be returned. Defaults to false.
 */
export const useGetSpaces = ({ own = false } = {}) => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const { setSpaces, setIsLoadingSpaces } = useData();

    const endpoint = ENDPOINT_URL + (own ? '/own' : '');

    const request = async (props: GetSpacesInput = {}) => {
        setIsLoadingSpaces(true);

        // Build query string
        const queryParams = new URLSearchParams();

        if (props.search) {
            queryParams.append('search', props.search);
        }
        if (props.spaceStatusUuid) {
            queryParams.append('spaceStatusUuid', props.spaceStatusUuid);
        }
        if (props.featuredType) {
            queryParams.append('featuredType', props.featuredType);
        }
        if (props.spaceTypeCategory) {
            queryParams.append('spaceTypeCategory', props.spaceTypeCategory);
        }
        if (props.priceMin !== undefined) {
            queryParams.append('priceMin', props.priceMin.toString());
        }
        if (props.priceMax !== undefined) {
            queryParams.append('priceMax', props.priceMax.toString());
        }
        if (props.areaMin !== undefined) {
            queryParams.append('areaMin', props.areaMin.toString());
        }
        if (props.areaMax !== undefined) {
            queryParams.append('areaMax', props.areaMax.toString());
        }
        if (props.spaceTypeUuids && props.spaceTypeUuids.length > 0) {
            props.spaceTypeUuids.forEach(uuid => {
                queryParams.append('spaceTypeUuids[]', uuid);
            });
        }
        if (props.page !== undefined) {
            queryParams.append('page', props.page.toString());
        }
        if (props.perPage !== undefined) {
            queryParams.append('perPage', props.perPage.toString());
        } else {
            queryParams.append('perPage', '6');
        }
        if (props.orderBy) {
            queryParams.append('orderBy', props.orderBy);
        }
        if (props.sortDesc !== undefined) {
            queryParams.append('sortDesc', props.sortDesc ? '1' : '0');
        }

        try {
            const response = await fetch(`${API_URL}${endpoint}?${queryParams.toString()}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetSpacesSuccessResponse = await response.json();

            setSpaces(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
        } finally {
            setIsLoadingSpaces(false);
        }
    };

    return { request };
};
